// iter420 — Customer Ledger export dialog.
// Two modes:
//   • Single customer (mode='single', customer required)
//   • All customers   (mode='all')
// User picks a date window + format (PDF / CSV) + delivery shape:
//   • single → PDF or CSV
//   • all    → Combined CSV (one file) or ZIP of PDFs or ZIP of CSVs
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText, FileSpreadsheet, Archive } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { toLocalYmd } from '@/lib/dateRange';

const PERIOD_OPTIONS = [
  { v: 'all', l: 'All time' },
  { v: 'this_month', l: 'This month' },
  { v: 'last_month', l: 'Last month' },
  { v: 'last_90', l: 'Last 90 days' },
  { v: 'ytd', l: 'Year to date' },
  { v: 'custom', l: 'Custom range…' },
];

// Convert a period choice into {date_from, date_to, month} params expected
// by the backend. Returns an empty object for 'all'.
function buildPeriodParams(period, dateFrom, dateTo) {
  const today = new Date();
  const fmt = (d) => toLocalYmd(d);
  if (period === 'all') return {};
  if (period === 'this_month') {
    return { month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}` };
  }
  if (period === 'last_month') {
    const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` };
  }
  if (period === 'last_90') {
    const from = new Date(today); from.setDate(from.getDate() - 90);
    return { date_from: fmt(from), date_to: fmt(today) };
  }
  if (period === 'ytd') {
    return { date_from: `${today.getFullYear()}-01-01`, date_to: fmt(today) };
  }
  // custom
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  return params;
}

function downloadBlob(data, filename, type) {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

export function LedgerExportDialog({ open, onOpenChange, mode = 'single', customer = null }) {
  const [period, setPeriod] = useState('this_month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState(mode === 'all' ? 'combined_csv' : 'pdf');
  const [busy, setBusy] = useState(false);

  // Reset the format default when the dialog is re-opened in a different
  // mode. Without this, a user who first opens "Export All Ledgers"
  // (default combined_csv) and later clicks the per-row Download icon
  // would still see combined_csv as the active chip — invalid for single
  // mode. Re-keying on `mode` and `open` keeps the dialog correct.
  useEffect(() => {
    if (open) {
      setFormat(mode === 'all' ? 'combined_csv' : 'pdf');
    }
  }, [mode, open]);

  const isAllMode = mode === 'all';

  const submit = async () => {
    const params = buildPeriodParams(period, dateFrom, dateTo);
    setBusy(true);
    try {
      if (!isAllMode) {
        // Single customer
        if (!customer?.id) {
          toast.error('No customer selected');
          return;
        }
        const path = format === 'pdf'
          ? `/customers/${customer.id}/report/pdf`
          : `/customers/${customer.id}/ledger.csv`;
        const r = await api.get(path, { params, responseType: 'blob' });
        const ext = format === 'pdf' ? 'pdf' : 'csv';
        const safe = (customer.name || customer.id).replace(/\s+/g, '_');
        downloadBlob(r.data, `ledger_${safe}.${ext}`, format === 'pdf' ? 'application/pdf' : 'text/csv');
      } else if (format === 'combined_csv') {
        // All customers combined CSV
        const r = await api.get('/customers/ledger-bulk-combined.csv', { params, responseType: 'blob' });
        downloadBlob(r.data, 'customer_ledgers_combined.csv', 'text/csv');
      } else if (format === 'csv_zip') {
        // All customers ZIP of CSVs
        const r = await api.post('/customers/ledger-bulk-csv-zip', params, { responseType: 'blob' });
        downloadBlob(r.data, 'customer_ledgers.zip', 'application/zip');
      } else if (format === 'pdf_zip') {
        // All customers ZIP of PDFs (uses min_balance=0 so everyone is included)
        const r = await api.post('/customers/statements/bulk-pdf-zip', { ...params, min_balance: 0 }, { responseType: 'blob' });
        downloadBlob(r.data, 'customer_statements.zip', 'application/zip');
      }
      toast.success('Export downloaded');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const title = isAllMode ? 'Export All Customer Ledgers' : `Export Ledger — ${customer?.name || ''}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ledger-export-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Pick a date window and a format. The export filters both sales and customer payments to the selected period.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="mt-1" data-testid="ledger-export-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ledger-date-from" className="text-xs">From</Label>
                <Input id="ledger-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="ledger-export-from" />
              </div>
              <div>
                <Label htmlFor="ledger-date-to" className="text-xs">To</Label>
                <Input id="ledger-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="ledger-export-to" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Format</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {!isAllMode && (
                <>
                  <FormatChip active={format === 'pdf'} onClick={() => setFormat('pdf')} icon={<FileText size={12} />} label="PDF Statement" testid="fmt-pdf" />
                  <FormatChip active={format === 'csv'} onClick={() => setFormat('csv')} icon={<FileSpreadsheet size={12} />} label="CSV Ledger" testid="fmt-csv" />
                </>
              )}
              {isAllMode && (
                <>
                  <FormatChip active={format === 'combined_csv'} onClick={() => setFormat('combined_csv')} icon={<FileSpreadsheet size={12} />} label="Single combined CSV" testid="fmt-combined" />
                  <FormatChip active={format === 'csv_zip'} onClick={() => setFormat('csv_zip')} icon={<Archive size={12} />} label="ZIP of CSVs" testid="fmt-csv-zip" />
                  <FormatChip active={format === 'pdf_zip'} onClick={() => setFormat('pdf_zip')} icon={<Archive size={12} />} label="ZIP of PDFs" testid="fmt-pdf-zip" />
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ledger-export-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (period === 'custom' && !dateFrom && !dateTo)}
            data-testid="ledger-export-submit"
          >
            <Download size={14} className="mr-1.5" />
            {busy ? 'Exporting…' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormatChip({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs border transition-colors ${
        active
          ? 'bg-foreground text-background border-foreground'
          : 'bg-background text-foreground border-border hover:bg-muted'
      }`}
    >
      {icon} {label}
    </button>
  );
}

export default LedgerExportDialog;
